import * as v from 'valibot';

import {
	keepPreviousData,
	queryOptions,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query';

import { api } from '#client';
import { handleError, parseSchema } from '#client/validation';

import { ResultsAndCount } from './common.types';
import {
	SingleUser,
	User,
	UserBody,
	UserId,
	UsersQuery,
	UsersResults,
} from './users.types';

const getUsers = (query: UsersQuery): Promise<UsersResults> =>
	api
		.get('/admin/users', {
			params: query,
		})
		.then(parseSchema(ResultsAndCount(User)));

const getUserById = (userId: string): Promise<SingleUser> =>
	api.get(`/admin/user/${userId}`).then(parseSchema(SingleUser));

const getUserJobTitles = (): Promise<string[]> =>
	api.get(`/admin/job-titles`).then(parseSchema(v.array(v.string())));

const getReportsToUsers = (
	orgs: number[],
	editUserRoleId: string,
): Promise<User[]> =>
	api
		.get(`/admin/reports-to-users/`, {
			params: {
				status: 'Active',
				organisations: orgs,
				editUserRoleId,
			},
		})
		.then(parseSchema(ResultsAndCount(User)))
		.then((result) => result.results);

export const getUsersByEmail = (email: string): Promise<UsersResults> =>
	api
		.get('/admin/users', {
			params: {
				skip: 0,
				take: 10,
				emailAddress: email,
			},
		})
		.then(parseSchema(ResultsAndCount(User)));

export const userQueries = {
	all: ['users'],
	lists: () => [...userQueries.all, 'list'],
	list: (query: UsersQuery) =>
		queryOptions({
			queryKey: [...userQueries.lists(), query],
			queryFn: () => getUsers(query),
			placeholderData: keepPreviousData,
		}),
	ids: () => [...userQueries.all, 'id'],
	id: (userId?: string) =>
		queryOptions({
			queryKey: [...userQueries.ids(), userId],
			queryFn: () => getUserById(userId!),
			enabled: !!userId,
		}),
	jobTitles: () =>
		queryOptions({
			queryKey: [...userQueries.all, 'jobTitles'],
			queryFn: () => getUserJobTitles(),
		}),
	reportsTo: (orgs: number[], editUserRoleId: string) =>
		queryOptions({
			queryKey: [...userQueries.ids(), 'reportsTo', orgs, editUserRoleId],
			queryFn: () => getReportsToUsers(orgs, editUserRoleId),
			enabled: !!orgs.length,
		}),
	byEmail: (email?: string) =>
		queryOptions({
			queryKey: [...userQueries.all, 'byEmail', email],
			queryFn: () => getUsersByEmail(email!),
			enabled: !!email,
		}),
};

const postUser = (params: UserBody): Promise<UserId> =>
	api
		.post(`/admin/user`, { json: params })
		.then(parseSchema(UserId))
		.catch(handleError(Object.keys(params)));

const putUser = (userId: string, params: UserBody) =>
	api
		.put(`/admin/user/${userId}`, { json: params })
		.catch(handleError(Object.keys(params)));

const putUserActive = (userId: string, isActive: { isActive: boolean }) =>
	api
		.put(`/admin/user/${userId}/block`, { json: isActive })
		.catch(handleError(['status']));

export const useNewUserMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: UserBody) => postUser(params),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: userQueries.lists(),
			}),
	});
};

export const useEditUserMutate = (userId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: UserBody) => putUser(userId, params),
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: userQueries.lists(),
				}),
				queryClient.invalidateQueries(userQueries.id(userId)),
			]);
		},
	});
};

export const useUserActiveMutate = (userId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (isActive: { isActive: boolean }) =>
			putUserActive(userId, isActive),
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: userQueries.lists(),
				}),
				queryClient.invalidateQueries(userQueries.id(userId)),
			]);
		},
	});
};
